// src/utils/colorTheme.ts
// Áp dụng màu thương hiệu (primaryColor) làm CSS variable thực tế cho toàn app.
// Velzon định nghĩa các class như .btn-primary, .bg-primary, .text-primary...
// đều tham chiếu tới biến --vz-primary(-rgb|-text-emphasis|-bg-subtle|-border-subtle),
// nên chỉ cần override các biến này trên :root là sidebar/topbar/button đổi màu thật,
// không chỉ preview.

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export const isValidHexColor = (hex: string): boolean => HEX_RE.test(hex);

const hexToRgbArr = (hex: string): [number, number, number] => {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const rgbArrToHex = (r: number, g: number, b: number): string => {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

// Tương đương Sass mix($color1, $color2, $weight): weight% color1 + (100-weight)% color2
const mix = (hex1: string, hex2: string, weightPercent: number): string => {
  const [r1, g1, b1] = hexToRgbArr(hex1);
  const [r2, g2, b2] = hexToRgbArr(hex2);
  const w = weightPercent / 100;
  return rgbArrToHex(
    r1 * w + r2 * (1 - w),
    g1 * w + g2 * (1 - w),
    b1 * w + b2 * (1 - w)
  );
};

// Tương đương Sass shade-color($color, $weight): trộn với đen
export const shadeColor = (hex: string, weight: number = 15): string => mix('#000000', hex, weight);

// Tương đương Sass tint-color($color, $weight): trộn với trắng
export const tintColor = (hex: string, weight: number = 15): string => mix('#ffffff', hex, weight);

const PRIMARY_VARS = [
  '--vz-primary',
  '--vz-primary-rgb',
  '--vz-primary-text-emphasis',
  '--vz-primary-bg-subtle',
  '--vz-primary-border-subtle',
] as const;

/**
 * Áp dụng primaryColor làm màu chủ đạo thực sự của toàn app (button, badge, link, sidebar...)
 * bằng cách override các CSS custom property mà Velzon dùng cho theme color "primary".
 */
export const applyPrimaryBrandColor = (hex: string): void => {
  if (!isValidHexColor(hex)) return;
  const root = document.documentElement.style;
  const [r, g, b] = hexToRgbArr(hex);
  root.setProperty('--vz-primary', hex);
  root.setProperty('--vz-primary-rgb', `${r}, ${g}, ${b}`);
  root.setProperty('--vz-primary-text-emphasis', shadeColor(hex, 15));
  root.setProperty('--vz-primary-bg-subtle', tintColor(hex, 85));
  root.setProperty('--vz-primary-border-subtle', tintColor(hex, 60));
};

export const clearPrimaryBrandColor = (): void => {
  const root = document.documentElement.style;
  PRIMARY_VARS.forEach((prop) => root.removeProperty(prop));
};

const SIDEBAR_VARS = [
  '--vz-vertical-menu-bg',
  '--vz-vertical-menu-border',
  '--vz-twocolumn-menu-iconview-bg',
] as const;

/**
 * Áp dụng nền sidebar theo màu thương hiệu tuỳ chỉnh (chế độ "custom"),
 * tái sử dụng phong cách gradient sẵn có của sidebar (chữ/icon trắng).
 */
export const applyCustomSidebarBackground = (hex: string): void => {
  if (!isValidHexColor(hex)) return;
  const root = document.documentElement.style;
  const gradient = `linear-gradient(180deg, ${hex} 0%, ${shadeColor(hex, 25)} 100%)`;
  root.setProperty('--vz-vertical-menu-bg', gradient);
  root.setProperty('--vz-vertical-menu-border', shadeColor(hex, 10));
  root.setProperty('--vz-twocolumn-menu-iconview-bg', gradient);
};

export const clearCustomSidebarBackground = (): void => {
  const root = document.documentElement.style;
  SIDEBAR_VARS.forEach((prop) => root.removeProperty(prop));
};
